import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import DoNotDisturbOnRoundedIcon from "@mui/icons-material/DoNotDisturbOnRounded";
import NewReleasesRoundedIcon from "@mui/icons-material/NewReleasesRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { getReleaseNotes } from "../actions";
import AppPage from "./app/AppPage";
import { brand, notice } from "../theme/businessFlowsTheme";
import { EmptyState, Surface } from "./workspaces/WorkspaceUi";

// Four kinds of change, four tones from the shared notice palette - so "fix"
// here is the same green as a check that passed, and "deprecated" the same
// clay as anything else that wants reading twice. The brand yellow keeps its
// one job: it marks what is new.
const TYPE_META = {
  NEW: { label: "New", bg: brand.mark, line: brand.signalDeep, color: brand.ink, icon: <AutoAwesomeRoundedIcon /> },
  IMPROVEMENT: { label: "Improvement", ...notice.quiet, icon: <TrendingUpRoundedIcon /> },
  FIX: { label: "Fix", ...notice.good, icon: <BuildRoundedIcon /> },
  DEPRECATED: { label: "Deprecated", ...notice.fix, icon: <DoNotDisturbOnRoundedIcon /> },
};

// notice tones name their colours fg/bg; the chips below read color/bg.
const toneOf = (meta) => ({ color: meta.color || meta.fg, bg: meta.bg, line: meta.line });
const FILTERS = ["ALL", ...Object.keys(TYPE_META)];

// Dates are stored as YYYY-MM-DD; noon avoids showing the previous day in European time zones.
const formatDate = (date) =>
  date
    ? new Date(date + "T12:00:00").toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })
    : "";

const anchorId = (version) => `release-${String(version).replace(/[^a-zA-Z0-9]/g, "-")}`;

function ChangeItem({ change }) {
  const meta = TYPE_META[change.type] || TYPE_META.NEW;
  const tone = toneOf(meta);
  return (
    <Stack component="li" direction="row" spacing={1.5} alignItems="flex-start">
      <Chip
        size="small"
        icon={meta.icon}
        label={meta.label}
        sx={{
          bgcolor: tone.bg,
          color: tone.color,
          border: `1px solid ${tone.line}`,
          fontWeight: 600,
          minWidth: 118,
          justifyContent: "flex-start",
          flexShrink: 0,
          "& .MuiChip-icon": { color: tone.color, fontSize: 15 },
        }}
      />
      <Typography variant="body2" sx={{ pt: 0.3, lineHeight: 1.6, overflowWrap: "anywhere" }}>
        {change.text}
      </Typography>
    </Stack>
  );
}

function ReleaseCard({ note, latest, changes }) {
  return (
    <Paper
      id={anchorId(note.version)}
      elevation={0}
      sx={{
        scrollMarginTop: 16,
        p: { xs: 2.5, md: 3 },
        borderRadius: 4,
        border: `1px solid ${latest ? brand.ink : brand.line}`,
        position: "relative",
        overflow: "hidden",
        ...(latest && {
          "&::before": {
            content: '""',
            position: "absolute",
            inset: "0 0 auto 0",
            height: 4,
            bgcolor: brand.signal,
          },
        }),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
          <Chip
            size="small"
            label={`v${note.version}`}
            sx={{ bgcolor: latest ? brand.ink : brand.shell, color: latest ? "#fff" : brand.ink, fontWeight: 700 }}
          />
          {latest && (
            <Chip
              size="small"
              icon={<NewReleasesRoundedIcon />}
              label="Latest"
              sx={{ bgcolor: brand.mark, border: `1px solid ${brand.signalDeep}`, color: brand.ink, fontWeight: 600, "& .MuiChip-icon": { color: brand.ink } }}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {formatDate(note.date)}
        </Typography>
      </Stack>

      <Typography component="h2" sx={{ fontWeight: 700, fontSize: { xs: "1.15rem", md: "1.3rem" }, lineHeight: 1.3 }}>
        {note.title}
      </Typography>
      {note.summary && (
        <Typography color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.65, maxWidth: 720 }}>
          {note.summary}
        </Typography>
      )}

      {changes.length > 0 && (
        <Stack component="ul" spacing={1.25} sx={{ listStyle: "none", m: 0, mt: 2.5, p: 0 }}>
          {changes.map((change, i) => (
            <ChangeItem key={i} change={change} />
          ))}
        </Stack>
      )}
    </Paper>
  );
}

export default function ReleaseNotes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");

  // null means the read failed: say so, rather than claiming no release has
  // been published.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    getReleaseNotes().then((data) => {
      setFailed(data === null);
      setNotes(Array.isArray(data) ? data : []);
      setLoading(false);
    });
  }, []);

  const counts = useMemo(() => {
    const c = { ALL: 0 };
    notes.forEach((note) =>
      (note.changes || []).forEach((change) => {
        c.ALL += 1;
        c[change.type] = (c[change.type] || 0) + 1;
      })
    );
    return c;
  }, [notes]);

  const visible = notes
    .map((note, idx) => ({
      note,
      idx,
      changes: (note.changes || []).filter((change) => filter === "ALL" || change.type === filter),
    }))
    .filter(({ note, changes }) => filter === "ALL" || changes.length > 0);

  const jumpTo = (version) =>
    document.getElementById(anchorId(version))?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <AppPage title="What's new" description="Every Business Flows release, newest first." maxWidth={1120}>
      {loading ? (
        <Stack spacing={2}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={180} sx={{ borderRadius: 4 }} />
          ))}
        </Stack>
      ) : failed ? (
        <Surface sx={{ borderStyle: "dashed" }}>
          <EmptyState icon={<NewReleasesRoundedIcon />} title="The releases couldn't be loaded">
            Try again in a moment. If it keeps happening, tell bpExperts.
          </EmptyState>
        </Surface>
      ) : notes.length === 0 ? (
        <Surface sx={{ borderStyle: "dashed" }}>
          <EmptyState icon={<NewReleasesRoundedIcon />} title="No releases published yet">
            Every Business Flows release is written up here. Check back after the next one ships.
          </EmptyState>
        </Surface>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "220px minmax(0, 1fr)" },
            gap: { xs: 2, lg: 4 },
            alignItems: "start",
          }}
        >
          {/* Version index */}
          <Box component="nav" aria-label="Releases" sx={{ display: { xs: "none", lg: "block" }, position: "sticky", top: 16 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1, px: 1.5 }}>
              Releases
            </Typography>
            <Stack spacing={0.25}>
              {notes.map((note, idx) => (
                <ButtonBase
                  key={note.version || idx}
                  onClick={() => jumpTo(note.version)}
                  sx={{
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                    borderRadius: 2.5,
                    textAlign: "left",
                    "&:hover": { bgcolor: "#fff" },
                    "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: 2 },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: idx === 0 ? 700 : 500 }}>
                    v{note.version}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {note.date ? new Date(note.date + "T12:00:00").toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : ""}
                  </Typography>
                </ButtonBase>
              ))}
            </Stack>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: "wrap", rowGap: 1 }} role="group" aria-label="Filter changes">
              {FILTERS.map((key) => {
                const active = filter === key;
                const label = key === "ALL" ? "All changes" : TYPE_META[key].label;
                return (
                  <Chip
                    key={key}
                    label={`${label} ${counts[key] || 0}`}
                    onClick={() => setFilter(key)}
                    aria-pressed={active}
                    variant={active ? "filled" : "outlined"}
                    sx={{
                      fontWeight: 600,
                      bgcolor: active ? brand.ink : "#fff",
                      color: active ? "#fff" : brand.ink,
                      borderColor: brand.line,
                      "&:hover": { bgcolor: active ? brand.inkSoft : brand.canvas },
                    }}
                  />
                );
              })}
            </Stack>

            <Stack spacing={2}>
              {visible.map(({ note, idx, changes }) => (
                <ReleaseCard key={note.version || idx} note={note} latest={idx === 0} changes={changes} />
              ))}
              {visible.length === 0 && (
                <EmptyState
                  icon={<NewReleasesRoundedIcon />}
                  title="No releases include this kind of change"
                  action={<Button onClick={() => setFilter("ALL")}>Show all changes</Button>}
                >
                  Every release is still here — only this filter is empty.
                </EmptyState>
              )}
            </Stack>
          </Box>
        </Box>
      )}
    </AppPage>
  );
}
